import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { listCertifications } from "@/lib/certification";
import { CLERK_STATEMENTS_PATH, selectClerkStatementsForBrand } from "@/lib/clerkStatementRecords";
import { fetchFileRequests } from "@/lib/fileRequestsClient";
import { EMAIL_INTAKE_BRANDS, fetchEmailIntake } from "@/lib/emailIntake";
import { fetchFeeSlipPayments } from "@/lib/feeSlipPayments";
import { RECOUP_FORM_TYPE, recoupDraftId } from "@/lib/budgetRecoup";
import { AID_WEEK_FORM_TYPE } from "@/lib/billingAidStatus";
import { VALS_DIARY_FORM_TYPE } from "@/lib/valsDiaryWeeks";
import { asList, getJson, loadSources, perFirm, portalDrafts, sharedDrafts } from "@/lib/summarySources";
import {
  SAVED_FORM_TYPES,
  WEEKLY_FIRMS,
  WEEKLY_FIRM_NAMES,
  addWeeks,
  buildWeeklySummary,
  defaultWeek,
  mondayOf,
  money,
  weekLabel,
  weeklySummaryPrintHtml,
} from "@/lib/weeklySummary";

// The admin Weekly summary: what was actioned for each firm in a week, to print and give to
// the firm (src/lib/weeklySummary.js decides what goes in it). Every record is read once when
// the page opens; changing the firm or the week only re-sorts what was read.

const SOURCES = {
  payments: () => perFirm(WEEKLY_FIRMS, (brand) => fetchFeeSlipPayments(brand)),
  // One read of the clerk statements, shared out by firm.
  statementRows: () => getJson(`${CLERK_STATEMENTS_PATH}?limit=50`).then((payload) =>
    Object.fromEntries(WEEKLY_FIRMS.map((brand) => [brand, selectClerkStatementsForBrand(payload?.records || [], brand)]))),
  // Status changes on clerk statement rows over the past year, shared out by firm. A week is
  // a few dozen; Deadline Guard returns at most 2,000.
  statementChanges: () => getJson(`/api/render/api/clerk-statements/activity?${new URLSearchParams({
    since: new Date(Date.now() - 366 * 24 * 60 * 60 * 1000).toISOString(),
    until: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  })}`).then((payload) => Object.fromEntries(WEEKLY_FIRMS.map((brand) =>
    [brand, asList(payload?.changes).filter((change) => change?.firm === brand)]))),
  intake: () => perFirm(EMAIL_INTAKE_BRANDS, (brand) => fetchEmailIntake(brand)),
  backsheets: () => perFirm(WEEKLY_FIRMS, (brand) => sharedDrafts(brand, "backsheet")),
  fileRequests: () => perFirm(WEEKLY_FIRMS, (brand) => fetchFileRequests(brand).then((result) => result.requests || [])),
  requests: () => getJson("/api/portal-requests?limit=500").then(asList),
  certifications: () => perFirm(WEEKLY_FIRMS, (brand) => listCertifications(brand).then((body) => asList(body?.certifications))),
  recoup: () => perFirm(WEEKLY_FIRMS, (brand) =>
    sharedDrafts(brand, RECOUP_FORM_TYPE).then((rows) => rows.find((row) => row.id === recoupDraftId(brand))?.form_data || null)),
  aidWeeks: () => sharedDrafts("lacw", AID_WEEK_FORM_TYPE),
  diaryWeeks: () => perFirm(["vals", "eas"], (brand) => portalDrafts(brand, VALS_DIARY_FORM_TYPE)),
  forms: () => perFirm(WEEKLY_FIRMS, (brand) =>
    Promise.all(SAVED_FORM_TYPES.map((formType) => portalDrafts(brand, formType))).then((lists) => lists.flat())),
};

// Long sections show this many lines on screen until opened; the printout shows them all.
const SHOWN_LINES = 8;

const FIRM_CHOICES = [["all", "All firms"], ...WEEKLY_FIRMS.map((brand) => [brand, brand === "rga" ? "RGA" : WEEKLY_FIRM_NAMES[brand]])];

function FirmSummary({ summary, onOpen, primary }) {
  const [opened, setOpened] = useState(() => new Set());
  const withLines = summary.sections.filter((section) => section.lines.length);
  const empty = summary.sections.filter((section) => !section.lines.length).map((section) => section.title);
  return (
    <section className="mb-8 overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <h2 className="flex flex-wrap items-baseline justify-between gap-2 border-b border-neutral-200 bg-neutral-50 px-4 py-3">
        <span className="text-lg font-semibold text-neutral-900">{summary.firm}</span>
        <span className="text-xs text-neutral-500">{summary.count} item{summary.count === 1 ? "" : "s"} actioned</span>
      </h2>

      {summary.unavailable.length > 0 && (
        <p className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          Couldn't read {summary.unavailable.join(", ")} just now, so anything there may be missing. Refresh to try again.
        </p>
      )}

      {withLines.length === 0 && (
        <p className="px-4 py-4 text-sm text-neutral-500">Nothing was recorded as actioned this week.</p>
      )}

      {withLines.map((section) => (
        <div key={section.id} className="border-b border-neutral-200 last:border-b-0">
          <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pt-3 pb-1">
            <h3 className="text-sm font-semibold text-neutral-900">
              {section.title}{" "}
              <span className="font-normal text-neutral-500">
                · {section.lines.length}{section.total !== null && ` · ${money(section.total)}`}
              </span>
            </h3>
            <button
              type="button"
              onClick={() => onOpen(section.tab)}
              className="text-xs font-medium hover:underline"
              style={{ color: primary }}
            >
              {section.page} <span aria-hidden="true">→</span>
            </button>
          </div>
          {section.note && <p className="px-4 pb-1 text-xs italic text-neutral-500">{section.note}</p>}
          <ul className="pb-2">
            {(opened.has(section.id) ? section.lines : section.lines.slice(0, SHOWN_LINES)).map((entry, index) => (
              <li key={`${section.id}-${index}`} className="flex gap-3 px-4 py-1.5 text-sm">
                <span className="w-20 shrink-0 whitespace-nowrap text-xs leading-5 text-neutral-500">{entry.day}</span>
                <span className="min-w-0 flex-1 text-neutral-900">
                  {entry.text}
                  {entry.detail && <span className="block text-xs text-neutral-500">{entry.detail}</span>}
                </span>
                {"amount" in entry && (
                  <span className="whitespace-nowrap text-sm tabular-nums text-neutral-900">{money(entry.amount)}</span>
                )}
              </li>
            ))}
          </ul>
          {section.lines.length > SHOWN_LINES && !opened.has(section.id) && (
            <button
              type="button"
              onClick={() => setOpened((current) => new Set(current).add(section.id))}
              className="px-4 pb-3 text-xs font-medium hover:underline"
              style={{ color: primary }}
            >
              Show all {section.lines.length}
            </button>
          )}
        </div>
      ))}

      {withLines.length > 0 && empty.length > 0 && (
        <p className="border-t border-neutral-200 px-4 py-2.5 text-xs text-neutral-500">Nothing this week: {empty.join(", ")}.</p>
      )}
    </section>
  );
}

export default function WeeklySummary({ onOpen = () => {}, primary = "#2563eb" }) {
  const [firm, setFirm] = useState("all");
  const [monday, setMonday] = useState(() => defaultWeek());
  const [state, setState] = useState({ status: "loading", data: null, at: null });

  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: current.data ? "refreshing" : "loading" }));
    const data = await loadSources(SOURCES, "Weekly summary");
    setState({ status: "ready", data, at: new Date() });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const thisWeek = mondayOf(new Date());
  const summaries = useMemo(() => {
    if (!state.data) return [];
    const brands = firm === "all" ? WEEKLY_FIRMS : [firm];
    return brands.map((brand) => buildWeeklySummary(state.data, { brand, monday }));
  }, [state.data, firm, monday]);

  const print = () => {
    const win = window.open("", "_blank");
    if (!win) return toast.error("Allow pop-ups to print.");
    win.document.write(weeklySummaryPrintHtml(summaries));
    win.document.close();
  };

  return (
    <main className="mx-auto w-full max-w-5xl bg-white px-5 py-8 sm:px-8 sm:py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">Weekly summary</h1>
      <p className="mt-1 text-sm text-neutral-500">
        What was actioned for each firm, Monday to Sunday. Print it to give the firm a copy.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-lg border border-neutral-200 p-0.5" role="group" aria-label="Firm">
          {FIRM_CHOICES.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFirm(value)}
              aria-pressed={firm === value}
              className={`rounded-md px-3 py-1.5 text-sm transition ${firm === value ? "text-white" : "text-neutral-700 hover:bg-neutral-100"}`}
              style={firm === value ? { backgroundColor: primary } : undefined}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 p-0.5">
          <button
            type="button"
            onClick={() => setMonday((current) => addWeeks(current, -1))}
            className="rounded-md p-1.5 text-neutral-600 hover:bg-neutral-100"
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <span className="min-w-[10.5rem] text-center text-sm font-medium text-neutral-900">{weekLabel(monday)}</span>
          <button
            type="button"
            onClick={() => setMonday((current) => addWeeks(current, 1))}
            disabled={monday >= thisWeek}
            className="rounded-md p-1.5 text-neutral-600 hover:bg-neutral-100 disabled:opacity-30"
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {monday.getTime() === thisWeek.getTime() && <span className="text-xs text-neutral-500">This week so far</span>}

        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={state.status !== "ready"} className="gap-1.5">
            <RefreshCw className={`h-3.5 w-3.5 ${state.status !== "ready" ? "animate-spin" : ""}`} aria-hidden="true" />
            Refresh
          </Button>
          <Button type="button" size="sm" onClick={print} disabled={!summaries.length} className="gap-1.5" style={{ backgroundColor: primary }}>
            <Printer className="h-3.5 w-3.5" aria-hidden="true" />
            Print / PDF
          </Button>
        </div>
      </div>

      <div className="mt-6">
        {!state.data ? (
          <p className="text-sm text-neutral-500">Reading what was done across VALS, LACW, EAS and RGA…</p>
        ) : (
          summaries.map((summary) => <FirmSummary key={summary.brand} summary={summary} onOpen={onOpen} primary={primary} />)
        )}
      </div>

      {state.at && (
        <p className="text-xs text-neutral-400">
          Read at {state.at.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}. Only work saved with a date is counted.
        </p>
      )}
    </main>
  );
}
